import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CreditCard, Barcode, Smartphone, Tag, AlertCircle, Shield, Loader2, CheckCircle, Copy, ExternalLink, Download, FileText } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../contexts/CartContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CheckoutStep4 = () => {
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const [bookingData, setBookingData] = useState(null);
  const [travelersData, setTravelersData] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [installments, setInstallments] = useState(1);
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [couponBlocked, setCouponBlocked] = useState(false);
  const [hasPromotion, setHasPromotion] = useState(false);
  const [campaignMode, setCampaignMode] = useState(false);
  const [currentDailyDiscount, setCurrentDailyDiscount] = useState(0);
  const [maxInterestFree, setMaxInterestFree] = useState(2);
  const [cartCheckout, setCartCheckout] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [copied, setCopied] = useState(false);
  
  const [cardData, setCardData] = useState({
    holderName: '',
    number: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    postalCode: '',
    addressNumber: ''
  });

  useEffect(() => {
    // Modo Carrinho: múltiplos pacotes
    const cartRaw = sessionStorage.getItem('cartCheckout');
    if (cartRaw) {
      try {
        const cart = JSON.parse(cartRaw);
        if (cart?.items?.length > 0) {
          setCartCheckout(cart);
          // Constrói um bookingData "agregado" a partir do primeiro item para preservar contrato/categoria
          const firstItem = cart.items[0];
          const aggregated = {
            ...firstItem.bookingData,
            totalPrice: cart.total,
            packageName: cart.items.length === 1
              ? firstItem.packageName
              : `${cart.items.length} pacotes`,
            // Categoria do carrinho usada para regras (PIX/juros): se TODOS forem mesma categoria, usa essa; senão, "misto"
            packageCategory: cart.items.every(i => i.packageCategory === firstItem.packageCategory)
              ? firstItem.packageCategory
              : 'misto'
          };
          setBookingData(aggregated);
          if (firstItem.allTravelers && firstItem.allTravelers.length > 0) {
            setTravelersData(firstItem.allTravelers);
          }
          return;
        }
      } catch {}
    }

    // Modo Único (legado)
    const data = sessionStorage.getItem('bookingData');
    if (!data) {
      navigate('/');
      return;
    }
    const parsedData = JSON.parse(data);
    setBookingData(parsedData);
    
    // Os dados dos viajantes estão dentro do bookingData.allTravelers
    if (parsedData.allTravelers && parsedData.allTravelers.length > 0) {
      setTravelersData(parsedData.allTravelers);
    } else if (parsedData.personalData) {
      // Fallback: usar personalData como array
      setTravelersData([parsedData.personalData]);
    }
  }, [navigate]);

  // Bolsa de Crédito — carrega saldos do comprador
  const [creditBags, setCreditBags] = useState({ normal: 0, limited: 0 });
  const [useNormalCredit, setUseNormalCredit] = useState(false);
  const [useLimitedCredit, setUseLimitedCredit] = useState(false);
  const [creditPreview, setCreditPreview] = useState(null);
  const buyerCpf = (travelersData?.[0]?.cpf || bookingData?.personalData?.cpf || '').replace(/\D/g, '');

  useEffect(() => {
    if (buyerCpf.length !== 11) return;
    axios.get(`${API_URL}/api/credit-bags/${buyerCpf}`)
      .then(r => setCreditBags({
        normal: r.data?.normal?.balance || 0,
        limited: r.data?.limited?.balance || 0,
      }))
      .catch(() => {});
  }, [buyerCpf]);

  const isRodoviario = bookingData?.packageCategory === 'rodoviario';
  const isPasSeio1Dia = bookingData?.packageCategory === 'passeio-1-dia';
  const isAereo = bookingData?.packageCategory?.toLowerCase().includes('aereo') || 
                   bookingData?.packageCategory?.toLowerCase().includes('aéreo') ||
                   bookingData?.packageCategory === 'internacional';
  const minDaysForBoleto = isRodoviario ? 7 : 20;

  const getDaysUntilTrip = () => {
    if (!bookingData?.startDate) return 999;
    const tripDate = new Date(bookingData.startDate);
    const today = new Date();
    const diffTime = tripDate - today;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // Carregar configurações globais + verificar cupom
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const [settingsRes, promotionRes, siteSettingsRes] = await Promise.all([
          axios.get(`${API_URL}/api/themes/settings`),
          axios.get(`${API_URL}/api/themes/has-active-promotion`),
          axios.get(`${API_URL}/api/packages/site-settings`).catch(() => ({ data: {} }))
        ]);
        
        const blockSetting = settingsRes.data.block_coupon_during_promotion || false;
        const hasActivePromotion = promotionRes.data.has_promotion || false;
        
        setHasPromotion(hasActivePromotion);
        setCouponBlocked(blockSetting && hasActivePromotion);
        
        const maxFree = siteSettingsRes.data?.max_interest_free_installments;
        if (maxFree !== undefined && maxFree !== null) {
          setMaxInterestFree(parseInt(maxFree) || 2);
        }
        // Modo Campanha: desativa o desconto PIX à vista
        setCampaignMode(!!siteSettingsRes.data?.campaign_active);
        setCurrentDailyDiscount(Number(siteSettingsRes.data?.current_daily_discount) || 0);
      } catch (error) {
        console.error('Erro ao carregar configurações:', error);
      }
    };
    loadSettings();
  }, []);

  const getMonthsUntilTrip = () => {
    if (!bookingData?.startDate) return 999;
    const tripDate = new Date(bookingData.startDate);
    const today = new Date();
    const diffTime = tripDate - today;
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    return diffDays / 30; // Aproximação de meses
  };

  // Verifica se o desconto PIX é aplicável
  // Viagens Aéreas: desconto só se faltar mais de 4 meses
  // Viagens 1 dia/Rodoviárias: desconto só se faltar mais de 2 meses
  // Modo Campanha: NÃO aplica desconto PIX (cliente já está em promoção/campanha)
  const canApplyPixDiscount = () => {
    if (campaignMode) return false;
    const monthsUntil = getMonthsUntilTrip();
    if (isAereo) {
      return monthsUntil > 4;
    } else {
      // Rodoviário ou passeio 1 dia
      return monthsUntil > 2;
    }
  };

  const pixDiscountEligible = canApplyPixDiscount();

  const daysUntilTrip = getDaysUntilTrip();
  // Removida restrição - clientes podem comprar até o último dia
  const canPurchaseOnline = true;

  // Regra especial: viagens NÃO aéreas com menos de 30 dias e mais de 3 dias
  // Permite 2x no PIX ou 2x no boleto (50% no ato, 50% até 7 dias antes)
  const isSpecialInstallmentRule = !isAereo && daysUntilTrip <= 30 && daysUntilTrip > 3;

  // Calcula o máximo de parcelas (boleto/PIX) para um pacote arbitrário.
  // startDate: ISO string da viagem; category: string da categoria.
  const computeMaxInstallmentsForPackage = (startDate, category) => {
    if (!startDate) return 1;
    const tripDate = new Date(startDate);
    const today = new Date();
    const itemIsAereo = (category || '').toLowerCase().includes('aereo') ||
                        (category || '').toLowerCase().includes('aéreo') ||
                        category === 'internacional';
    const itemIsRodov = category === 'rodoviario';
    const itemIsPasseio = category === 'passeio-1-dia';
    const itemMinDays = itemIsRodov ? 7 : 20;
    const diffDays = Math.ceil((tripDate - today) / 86400000);
    // Regra especial: passeio/rodoviário com 4..30 dias = 2x máx.
    if (!itemIsAereo && diffDays <= 30 && diffDays > 3) return 2;
    if (diffDays <= itemMinDays) return 1;
    const deadlineDate = new Date(tripDate);
    deadlineDate.setDate(deadlineDate.getDate() - itemMinDays);
    const currentDay = today.getDate();
    let maxN = 0;
    for (let i = 0; i < 12; i++) {
      const dueDate = new Date(today.getFullYear(), today.getMonth() + i, currentDay);
      if (dueDate <= deadlineDate) maxN = i + 1;
      else break;
    }
    return Math.max(1, Math.min(maxN, 12));
  };

  const getMaxBoletoInstallments = () => {
    // Modo carrinho: máximo entre os itens (cada um respeita sua própria regra)
    if (cartCheckout?.items?.length > 0) {
      const caps = cartCheckout.items.map(it => computeMaxInstallmentsForPackage(it.bookingData?.startDate, it.packageCategory));
      return Math.max(1, Math.min(Math.max(...caps), 12));
    }
    // Regra especial para viagens não aéreas com menos de 30 dias
    if (isSpecialInstallmentRule) {
      return 2; // Máximo 2x
    }
    return computeMaxInstallmentsForPackage(bookingData?.startDate, bookingData?.packageCategory);
  };

  // Per-item caps usado para waterfall de parcelas em modo carrinho
  const getCartItemCaps = () => {
    if (!cartCheckout?.items?.length) return [];
    return cartCheckout.items.map(it => ({
      id: it.id,
      packageName: it.packageName,
      totalPrice: Number(it.totalPrice) || 0,
      maxInstallments: computeMaxInstallmentsForPackage(it.bookingData?.startDate, it.packageCategory)
    }));
  };

  // Gera o cronograma de parcelas para N parcelas no modo carrinho.
  // Cada pacote é dividido em min(N, item.maxInstallments) parcelas iguais.
  // O valor da parcela K é a SOMA dos pacotes que ainda têm parcela ativa em K.
  //
  // IMPORTANTE: aplica um fator de escala proporcional para que a soma das parcelas
  // seja igual ao final discountedTotal (cupom + PIX + tier + créditos + juros).
  const buildCartInstallmentSchedule = (numInstallments) => {
    const caps = getCartItemCaps();
    if (!caps.length) return [];

    // Preço final que deve casar com a soma das parcelas.
    // Para boleto NÃO usamos o desconto PIX (pixDiscount é exclusivo de PIX).
    const pricing = calculateFinalPrice(numInstallments);
    const originalSum = caps.reduce((s, c) => s + (Number(c.totalPrice) || 0), 0);

    // Total que o cliente vai pagar via boleto: após cupom/tier/juros mas SEM desconto PIX
    // e SEM crédito (créditos abatem valor mas não aparecem em boletos).
    const boletoTotal = Math.max(0, Math.round((
      (pricing.baseTotal || originalSum)
      - (pricing.cartTierDiscount || 0)
      - (pricing.couponDiscount || 0)
      - (pricing.creditUsed || 0)
      + (pricing.interestAmount || 0)
    ) * 100) / 100);

    // Fator para redistribuir proporcionalmente entre os pacotes.
    // Evita divisão por zero.
    const scale = originalSum > 0 ? (boletoTotal / originalSum) : 1;

    const schedule = [];
    for (let k = 1; k <= numInstallments; k++) {
      let totalK = 0;
      const parts = [];
      for (const c of caps) {
        const itemN = Math.min(numInstallments, c.maxInstallments);
        if (k <= itemN) {
          const rawPart = (c.totalPrice * scale) / itemN;
          const part = Math.round(rawPart * 100) / 100;
          totalK += part;
          parts.push({ id: c.id, name: c.packageName, value: part });
        }
      }
      schedule.push({ installment: k, value: Math.round(totalK * 100) / 100, parts });
    }

    // Ajuste de centavos: garantir que sum(schedule) === boletoTotal
    const sumSchedule = schedule.reduce((s, x) => s + x.value, 0);
    const diff = Math.round((boletoTotal - sumSchedule) * 100) / 100;
    if (schedule.length > 0 && Math.abs(diff) >= 0.01) {
      // Ajusta a última parcela para cobrir os centavos residuais.
      schedule[schedule.length - 1].value = Math.round((schedule[schedule.length - 1].value + diff) * 100) / 100;
    }
    return schedule;
  };

  // Para PIX parcelado na regra especial
  const getMaxPixInstallments = () => {
    if (isSpecialInstallmentRule) {
      return 2; // Máximo 2x no PIX também
    }
    return 1; // PIX normalmente é à vista
  };

  const maxBoletoInstallments = getMaxBoletoInstallments();
  const maxCardInstallments = 12;

  // Taxas base de juros por número de parcelas
  const baseInterestRates = {
    1: 0, 2: 0, 3: 2.99, 4: 3.99, 5: 4.99,
    6: 5.99, 7: 6.99, 8: 7.99, 9: 8.99,
    10: 9.99, 11: 10.99, 12: 11.99
  };
  
  // Taxas aplicadas: sem juros até maxInterestFree, juros a partir daí
  const getInterestRate = (numInstallments) => {
    if (numInstallments <= maxInterestFree) return 0;
    // Parcelas acima do limite: usar taxa base se existir e > 0, senão calcular
    const baseRate = baseInterestRates[numInstallments] || 0;
    if (baseRate > 0) return baseRate;
    // Para parcelas que normalmente são sem juros (1x, 2x) mas agora excedem o limite
    return Math.max(1.99, (numInstallments - 1) * 1.5);
  };

  // ============================================
  // FUNÇÃO CENTRALIZADA DE CÁLCULO DE PREÇO
  // Toda a UI deve usar APENAS esta função
  // ============================================
  const calculateFinalPrice = (numInstallments = installments) => {
    const baseTotal = bookingData?.totalPrice || 0;

    // 0. Desconto progressivo do carrinho durante Modo Campanha
    // Conta TOTAL DE VIAJANTES (não apenas itens) — cada passageiro = "1 pacote" para o tier.
    // 1 viajante = 5%, 2 = 10%, 3+ = 15%
    //
    // IMPORTANTE: o desconto diário global já está aplicado ao preço do pacote (via sync).
    // Então só aplicamos a DIFERENÇA até o efetivo máximo. Nunca dobra desconto.
    let cartTierDiscount = 0;
    let cartTierPercent = 0;
    let tierSource = '';
    if (campaignMode) {
      let totalTravelers = 0;
      if (cartCheckout?.items?.length > 0) {
        totalTravelers = cartCheckout.items.reduce((s, it) => s + (Number(it.travelersCount) || 1), 0);
      } else if (bookingData) {
        totalTravelers = (bookingData.allTravelers && bookingData.allTravelers.length) || bookingData.quantity || 1;
      }
      const tierPct = totalTravelers >= 3 ? 15 : totalTravelers === 2 ? 10 : 5;
      const dailyPct = Number(currentDailyDiscount) || 0;
      const effectivePct = Math.max(dailyPct, tierPct);

      // Reconstroi o preço original (antes do desconto diário aplicado em DB)
      const originalBeforeDaily = dailyPct > 0
        ? baseTotal / (1 - dailyPct / 100)
        : baseTotal;
      // Total alvo após aplicar o desconto efetivo
      const targetTotal = Math.round(originalBeforeDaily * (1 - effectivePct / 100) * 100) / 100;
      // Desconto adicional necessário (pode ser 0 se daily ≥ tier)
      const additional = Math.max(0, Math.round((baseTotal - targetTotal) * 100) / 100);

      cartTierPercent = effectivePct;
      cartTierDiscount = additional;
      if (dailyPct >= tierPct) {
        tierSource = additional > 0 ? 'daily' : 'daily-already-applied';
      } else {
        tierSource = `tier-${totalTravelers}`;
      }
    }
    const afterTier = Math.round((baseTotal - cartTierDiscount) * 100) / 100;

    // 1. Aplicar cupom (BLOQUEADO durante campanha)
    // Em modo carrinho com cupom de categoria/pacote específico,
    // aplica APENAS sobre os itens elegíveis.
    let couponDiscount = 0;
    let couponEligibleBase = afterTier;
    if (couponApplied && !campaignMode) {
      if (cartCheckout?.items?.length > 0 && couponApplied.applies_to && couponApplied.applies_to !== 'all') {
        const applies = couponApplied.applies_to;
        const specificIds = couponApplied.specific_package_ids || [];
        const eligibleItems = cartCheckout.items.filter(it => {
          if (applies === 'specific') return specificIds.includes(it.packageId);
          return it.packageCategory === applies;
        });
        couponEligibleBase = eligibleItems.reduce((s, it) => s + (Number(it.totalPrice) || 0), 0);
        // Proporção do desconto sobre a parte elegível (que pode já ter sofrido a tier discount)
        if (afterTier > 0 && baseTotal > 0) {
          couponEligibleBase = Math.round(couponEligibleBase * (afterTier / baseTotal) * 100) / 100;
        }
      }
      if (couponApplied.type === 'percentage') {
        couponDiscount = Math.round(couponEligibleBase * couponApplied.value / 100 * 100) / 100;
      } else {
        couponDiscount = Math.min(couponApplied.value, couponEligibleBase);
      }
    }
    const afterCoupon = Math.round((afterTier - couponDiscount) * 100) / 100;
    
    // 2. Aplicar desconto PIX (sobre valor após cupom, SEM juros)
    let pixDiscount = 0;
    const hasPixDiscount = paymentMethod === 'pix' && pixDiscountEligible;
    if (hasPixDiscount) {
      pixDiscount = Math.round(afterCoupon * 0.05 * 100) / 100;
    }
    const afterPixDiscount = Math.round((afterCoupon - pixDiscount) * 100) / 100;
    
    // 3. Aplicar juros (cartão acima do limite sem juros, sobre valor após cupom)
    let interestAmount = 0;
    const rate = getInterestRate(numInstallments);
    if (paymentMethod === 'cartao' && rate > 0) {
      interestAmount = Math.round(afterCoupon * rate / 100 * 100) / 100;
    }
    
    // 4. Valor final
    // PIX: afterCoupon - pixDiscount (sem juros)
    // Cartão: afterCoupon + juros (sem PIX)
    // Boleto: afterCoupon (sem juros, sem PIX)
    let finalPrice;
    if (paymentMethod === 'pix') {
      finalPrice = afterPixDiscount;
    } else if (paymentMethod === 'cartao') {
      finalPrice = Math.round((afterCoupon + interestAmount) * 100) / 100;
    } else {
      finalPrice = afterCoupon;
    }

    // 4.5 Aplicar Bolsa de Crédito (Normal e/ou Limitada — regras INDEPENDENTES)
    let normalUsed = 0;
    let limitedUsed = 0;
    if (useNormalCredit && creditBags.normal > 0) {
      normalUsed = Math.min(creditBags.normal, finalPrice);
    }
    if (useLimitedCredit && creditBags.limited > 0) {
      const maxByLimit = Math.round(finalPrice * 0.30 * 100) / 100;
      limitedUsed = Math.min(creditBags.limited, maxByLimit);
    }
    // Se as duas cobrem mais que o total, prioriza Limitada (respeita cap) e ajusta a Normal
    let totalCreditUsed = normalUsed + limitedUsed;
    if (totalCreditUsed > finalPrice) {
      normalUsed = Math.max(0, finalPrice - limitedUsed);
      totalCreditUsed = normalUsed + limitedUsed;
    }
    normalUsed = Math.round(normalUsed * 100) / 100;
    limitedUsed = Math.round(limitedUsed * 100) / 100;
    const creditUsed = Math.round(totalCreditUsed * 100) / 100;
    finalPrice = Math.max(0, Math.round((finalPrice - creditUsed) * 100) / 100);
    
    // 5. Valor da parcela
    const installmentValue = numInstallments > 0 
      ? Math.round(finalPrice / numInstallments * 100) / 100 
      : finalPrice;
    
    return {
      baseTotal,
      cartTierDiscount,
      cartTierPercent,
      cartTierSource: tierSource,
      couponDiscount,
      afterCoupon,
      pixDiscount,
      hasPixDiscount,
      interestRate: rate,
      interestAmount,
      creditUsed,
      normalUsed,
      limitedUsed,
      creditKindApplied: (normalUsed > 0 && limitedUsed > 0) ? 'both' : (normalUsed > 0 ? 'normal' : (limitedUsed > 0 ? 'limited' : null)),
      finalPrice,
      installmentValue,
      installments: numInstallments
    };
  };

  // Compatibilidade: calculateTotal() retorna o valor final
  const calculateTotal = () => calculateFinalPrice().finalPrice;

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price);
  };

  const applyCoupon = async () => {
    const upperCode = couponCode.toUpperCase().trim();
    if (!upperCode) {
      alert('Digite um código de cupom');
      return;
    }
    if (campaignMode) {
      alert('Cupons estão desabilitados durante a campanha promocional.');
      return;
    }
    
    try {
      const totalBeforeDiscount = bookingData?.totalPrice || 0;
      const isCart = !!(cartCheckout && cartCheckout.items?.length > 0);
      const params = new URLSearchParams({ total: String(totalBeforeDiscount) });
      if (isCart) params.append('cart_mode', 'true');
      else {
        if (bookingData?.packageId) params.append('package_id', bookingData.packageId);
        if (bookingData?.packageCategory) params.append('package_category', bookingData.packageCategory);
      }
      const response = await fetch(`${API_URL}/api/coupons/validate/${upperCode}?${params.toString()}`);
      
      if (!response.ok) {
        const error = await response.json();
        alert(error.detail || 'Cupom inválido ou expirado');
        return;
      }
      
      const couponData = await response.json();
      setCouponApplied({ 
        type: couponData.discount_type, 
        value: couponData.discount_value, 
        code: couponData.code,
        calculatedDiscount: couponData.calculated_discount,
        applies_to: couponData.applies_to || 'all',
        specific_package_ids: couponData.specific_package_ids || []
      });
    } catch (error) {
      console.error('Erro ao validar cupom:', error);
      alert('Erro ao validar cupom. Tente novamente.');
    }
  };

  const removeCoupon = () => {
    setCouponApplied(null);
    setCouponCode('');
  };

  // Criar reserva no backend
  const createReservation = async () => {
    const mainTraveler = travelersData?.[0] || {};

    // CART MODE: cria uma reserva para CADA item do carrinho
    if (cartCheckout?.items?.length > 0) {
      const pricing = calculateFinalPrice();
      const cartGroupId = `cart_${Date.now()}`;
      const created = [];
      const itemCaps = getCartItemCaps();
      // Para boleto/pix parcelado: cada item parcelado em min(installments, item.maxInstallments)
      const buildItemSchedule = (itemCap) => {
        const itemN = Math.min(installments, itemCap.maxInstallments);
        const today = new Date();
        const dueDates = [];
        for (let k = 0; k < itemN; k++) {
          const due = new Date(today.getFullYear(), today.getMonth() + k, today.getDate());
          dueDates.push({
            installment: k + 1,
            total_installments: itemN,
            due_date: due.toISOString().slice(0, 10),
            value: Math.round((itemCap.totalPrice / itemN) * 100) / 100
          });
        }
        return dueDates;
      };
      for (const item of cartCheckout.items) {
        const itemBooking = item.bookingData || {};
        const travelers = item.allTravelers && item.allTravelers.length > 0 ? item.allTravelers : [item.personalData].filter(Boolean);
        const formattedTravelersItem = travelers.map((t, index) => ({
          name: t.fullName || t.name || '',
          cpf: (t.cpf || '').replace(/\D/g, ''),
          birth_date: t.birthDay && t.birthMonth && t.birthYear
            ? `${t.birthDay.toString().padStart(2, '0')}/${t.birthMonth.toString().padStart(2, '0')}/${t.birthYear}`
            : '',
          email: t.email || '',
          phone: (t.phone || '').replace(/\D/g, ''),
          gender: t.gender || '',
          document_type: t.documentType || '',
          document_number: t.documentNumber || '',
          is_main: index === 0
        }));
        const mainItem = travelers[0] || {};
        const itemCap = itemCaps.find(c => c.id === item.id) || { maxInstallments: 1, totalPrice: item.totalPrice };
        const itemSchedule = (paymentMethod === 'boleto' && installments > 1) ? buildItemSchedule(itemCap) : [];
        const reservationData = {
          package_id: item.packageId,
          date_id: item.dateId,
          customer_name: mainItem.fullName || mainItem.name || mainTraveler.fullName || 'Cliente',
          customer_email: mainItem.email || mainTraveler.email || '',
          customer_phone: (mainItem.phone || mainItem.whatsapp || mainTraveler.phone || '').replace(/\D/g, ''),
          customer_cpf: (mainItem.cpf || mainTraveler.cpf || '').replace(/\D/g, ''),
          number_of_people: travelers.length || itemBooking.quantity || 1,
          room_type: itemBooking.roomType || 'compartilhado',
          boarding_point: itemBooking.boardingInfo?.boardingPoint || itemBooking.boardingPoint || '',
          city_departure: itemBooking.boardingInfo?.cityDeparture || itemBooking.cityDeparture || '',
          travelers: formattedTravelersItem,
          coupon_code: (!campaignMode && couponApplied?.code) || null,
          coupon_discount_type: (!campaignMode && couponApplied?.type) || null,
          coupon_discount_value: (!campaignMode && couponApplied?.value) || null,
          coupon_discount_amount: null,
          pix_discount_applied: false,
          pix_discount_percentage: null,
          pix_discount_amount: null,
          discount_amount: null,
          original_total: item.totalPrice,
          final_total: item.totalPrice,
          payment_method: paymentMethod,
          total_installments: Math.min(installments, itemCap.maxInstallments),
          installment_due_dates: itemSchedule,
          cart_group_id: cartGroupId,
          cart_total: pricing.finalPrice,
          transport_mode: itemBooking?.transportMode || 'with_air'
        };
        try {
          const r = await axios.post(`${API_URL}/api/reservations/`, reservationData);
          created.push(r.data);
        } catch (err) {
          const errorMsg = err.response?.data?.detail || err.message || 'Erro ao criar reserva (carrinho)';
          throw new Error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
        }
      }
      // Retorna a primeira reserva como "principal" (ASAAS cobra o total)
      const primary = created[0];
      primary.cart_reservation_ids = created.map(r => r.id);
      primary.cart_group_id = cartGroupId;
      return primary;
    }

    // SINGLE MODE: comportamento original
    // Formatar todos os viajantes para salvar na reserva
    const formattedTravelers = (travelersData || []).map((t, index) => ({
      name: t.fullName || t.name || '',
      cpf: (t.cpf || '').replace(/\D/g, ''),
      birth_date: t.birthDay && t.birthMonth && t.birthYear 
        ? `${t.birthDay.toString().padStart(2, '0')}/${t.birthMonth.toString().padStart(2, '0')}/${t.birthYear}`
        : '',
      email: t.email || '',
      phone: (t.phone || '').replace(/\D/g, ''),
      gender: t.gender || '',
      document_type: t.documentType || '',
      document_number: t.documentNumber || '',
      is_main: index === 0
    }));
    
    // Calcular valor final usando a função centralizada
    const pricing = calculateFinalPrice();
    const originalTotal = pricing.baseTotal;
    const finalTotal = pricing.finalPrice;
    const couponDiscountAmount = pricing.couponDiscount;
    const pixDiscountAmount = pricing.pixDiscount;
    const hasPixDiscount = pricing.hasPixDiscount;
    const totalDiscountAmount = couponDiscountAmount + pixDiscountAmount;
    
    const reservationData = {
      package_id: bookingData.packageId,
      date_id: bookingData.dateId,
      customer_name: mainTraveler.fullName || mainTraveler.name || 'Cliente',
      customer_email: mainTraveler.email || '',
      customer_phone: (mainTraveler.phone || mainTraveler.whatsapp || '').replace(/\D/g, ''),
      customer_cpf: (mainTraveler.cpf || '').replace(/\D/g, ''),
      number_of_people: bookingData.quantity || 1,
      room_type: bookingData.roomType || 'compartilhado',
      // Corrigido: pegar do boardingInfo corretamente
      boarding_point: bookingData.boardingInfo?.boardingPoint || bookingData.boardingPoint || '',
      city_departure: bookingData.boardingInfo?.cityDeparture || bookingData.cityDeparture || '',
      travelers: formattedTravelers,
      // Informações de cupom
      coupon_code: couponApplied?.code || null,
      coupon_discount_type: couponApplied?.type || null,
      coupon_discount_value: couponApplied?.value || null,
      coupon_discount_amount: couponDiscountAmount > 0 ? couponDiscountAmount : null,
      // Informações de desconto PIX
      pix_discount_applied: hasPixDiscount,
      pix_discount_percentage: hasPixDiscount ? 5 : null,
      pix_discount_amount: pixDiscountAmount > 0 ? pixDiscountAmount : null,
      // Totais
      discount_amount: totalDiscountAmount > 0 ? totalDiscountAmount : null,
      original_total: originalTotal,
      final_total: finalTotal,
      // Dados de Pagamento
      payment_method: paymentMethod,
      total_installments: installments,
      installment_due_dates: [], // Será preenchido depois do pagamento
      transport_mode: bookingData.transportMode || 'with_air',
      credit_bag_kind: pricing.creditKindApplied || null,
      credit_bag_used: pricing.creditUsed || 0,
      credit_bag_normal_used: pricing.normalUsed || 0,
      credit_bag_limited_used: pricing.limitedUsed || 0
    };

    try {
      const response = await axios.post(`${API_URL}/api/reservations/`, reservationData);
      // Debita cada bolsa separadamente
      const debits = [];
      if (pricing.normalUsed > 0 && buyerCpf) {
        debits.push({ kind: 'normal', amount: pricing.normalUsed });
      }
      if (pricing.limitedUsed > 0 && buyerCpf) {
        debits.push({ kind: 'limited', amount: pricing.limitedUsed });
      }
      for (const d of debits) {
        try {
          await axios.post(`${API_URL}/api/credit-bags/adjust`, {
            cpf: buyerCpf,
            kind: d.kind,
            amount: -d.amount,
            source: 'checkout',
            reservation_id: response.data.id,
            note: `Uso na reserva ${response.data.id}`
          });
        } catch (_) { /* não bloquear checkout se debit falhar */ }
      }
      // Atualiza saldos locais
      if (debits.length) {
        setCreditBags(cb => ({
          normal: Math.max(0, (cb.normal || 0) - (pricing.normalUsed || 0)),
          limited: Math.max(0, (cb.limited || 0) - (pricing.limitedUsed || 0)),
        }));
      }
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Erro ao criar reserva';
      throw new Error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
  };

  // Processar pagamento PIX via ASAAS
  const processPixPayment = async () => {
    setProcessing(true);
    try {
      const reservation = await createReservation();
      const mainTraveler = travelersData?.[0] || {};

      const response = await axios.post(`${API_URL}/api/asaas/pix`, {
        reservation_id: reservation.id,
        customer: {
          name: mainTraveler.fullName || mainTraveler.name || 'Cliente',
          email: mainTraveler.email || '',
          cpf: (mainTraveler.cpf || '').replace(/\D/g, ''),
          phone: (mainTraveler.phone || mainTraveler.whatsapp || '').replace(/\D/g, '')
        },
        value: calculateFinalPrice().finalPrice,
        description: `Reserva ${bookingData.packageName} - ${reservation.id}`
      });

      setPaymentResult({
        type: 'pix',
        ...response.data,
        reservationId: reservation.id
      });
      if (cartCheckout) { clearCart(); sessionStorage.removeItem('cartCheckout'); }
    } catch (error) {
      console.error('Erro PIX:', error);
      alert('Não foi possível gerar o PIX no momento. Por favor, entre em contato pelo WhatsApp (21) 97965-2791 para finalizar sua reserva.');
    } finally {
      setProcessing(false);
    }
  };

  // Processar pagamento Boleto
  const processBoletoPayment = async () => {
    setProcessing(true);
    try {
      const reservation = await createReservation();
      const mainTraveler = travelersData?.[0] || {};

      // Cart mode: monta installment_schedule consolidado com valores variáveis
      let installmentSchedule = null;
      let cartReservationIds = null;
      if (cartCheckout?.items?.length > 0 && installments > 1) {
        const schedule = buildCartInstallmentSchedule(installments);
        const today = new Date();
        installmentSchedule = schedule.map(s => {
          const due = new Date(today.getFullYear(), today.getMonth() + (s.installment - 1), today.getDate());
          return {
            installment: s.installment,
            value: s.value,
            due_date: due.toISOString().slice(0, 10)
          };
        });
        cartReservationIds = reservation.cart_reservation_ids || [reservation.id];
      }

      const response = await axios.post(`${API_URL}/api/asaas/boleto`, {
        reservation_id: reservation.id,
        customer: {
          name: mainTraveler.fullName || mainTraveler.name || 'Cliente',
          email: mainTraveler.email || '',
          cpf: (mainTraveler.cpf || '').replace(/\D/g, ''),
          phone: (mainTraveler.phone || mainTraveler.whatsapp || '').replace(/\D/g, '')
        },
        value: calculateTotal(),
        description: `Reserva ${bookingData.packageName}`,
        due_days: 5,
        installments: installments,
        installment_schedule: installmentSchedule,
        cart_reservation_ids: cartReservationIds
      });

      setPaymentResult({
        type: 'boleto',
        ...response.data,
        reservationId: reservation.id,
        installments: installments
      });
    } catch (error) {
      console.error('Erro Boleto:', error);
      alert('Não foi possível gerar o Boleto no momento. Por favor, entre em contato pelo WhatsApp (21) 97965-2791 para finalizar sua reserva.');
    } finally {
      setProcessing(false);
    }
  };

  // Processar pagamento Cartão
  const processCreditCardPayment = async () => {
    if (!cardData.holderName || !cardData.number || !cardData.expiryMonth || !cardData.expiryYear || !cardData.cvv) {
      alert('Preencha todos os dados do cartão');
      return;
    }
    if (!cardData.postalCode || !cardData.addressNumber) {
      alert('Preencha o CEP e número do endereço de cobrança');
      return;
    }

    setProcessing(true);
    try {
      const reservation = await createReservation();
      const mainTraveler = travelersData?.[0] || {};

      const response = await axios.post(`${API_URL}/api/asaas/credit-card`, {
        reservation_id: reservation.id,
        customer: {
          name: mainTraveler.fullName || mainTraveler.name || 'Cliente',
          email: mainTraveler.email || '',
          cpf: (mainTraveler.cpf || '').replace(/\D/g, ''),
          phone: (mainTraveler.phone || mainTraveler.whatsapp || '').replace(/\D/g, '')
        },
        value: calculateTotal(),
        card: {
          holder_name: cardData.holderName,
          number: cardData.number.replace(/\s/g, ''),
          expiry_month: cardData.expiryMonth,
          expiry_year: cardData.expiryYear,
          ccv: cardData.cvv
        },
        holder: {
          cpf: (mainTraveler.cpf || '').replace(/\D/g, ''),
          email: mainTraveler.email || '',
          phone: (mainTraveler.phone || mainTraveler.whatsapp || '').replace(/\D/g, ''),
          postal_code: cardData.postalCode.replace(/\D/g, ''),
          address_number: cardData.addressNumber
        },
        installments: installments,
        description: `Reserva ${bookingData.packageName}`
      });

      setPaymentResult({
        type: 'cartao',
        ...response.data,
        reservationId: reservation.id
      });
      if (cartCheckout) { clearCart(); sessionStorage.removeItem('cartCheckout'); }
    } catch (error) {
      const errorDetail = error.response?.data?.detail || error.message || 'Erro desconhecido';
      const errorMessage = typeof errorDetail === 'object' ? JSON.stringify(errorDetail) : errorDetail;
      alert('Erro no pagamento: ' + errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  const handlePayment = () => {
    if (!paymentMethod) {
      alert('Selecione uma forma de pagamento');
      return;
    }

    switch (paymentMethod) {
      case 'pix':
        processPixPayment();
        break;
      case 'boleto':
        processBoletoPayment();
        break;
      case 'cartao':
        processCreditCardPayment();
        break;
      default:
        alert('Método de pagamento inválido');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Função para baixar o contrato
  const handleDownloadContract = () => {
    const contractContent = bookingData?.contractContent;
    if (!contractContent) {
      alert('Contrato não disponível para download');
      return;
    }

    const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Contrato - TÔ INDO ALI</title>
  <style>
    body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
    h1 { text-align: center; color: #003366; }
    h2 { text-align: center; color: #666; font-size: 1.1rem; }
    h3 { color: #003366; margin-top: 20px; }
    .customer-info { background: #f5f5f5; padding: 15px; border-radius: 8px; margin: 15px 0; }
    ul { margin-left: 20px; }
    .signature-date { text-align: right; margin-top: 30px; font-style: italic; }
    .payment-info { background: #e8f5e9; padding: 15px; border-radius: 8px; margin: 20px 0; border: 1px solid #4caf50; }
  </style>
</head>
<body>
  ${contractContent}
  <div class="payment-info">
    <h3>✅ PAGAMENTO REALIZADO</h3>
    <p><strong>Reserva:</strong> ${paymentResult?.reservationId || 'N/A'}</p>
    <p><strong>Método:</strong> ${paymentMethod === 'pix' ? 'PIX' : paymentMethod === 'boleto' ? 'Boleto' : 'Cartão de Crédito'}</p>
    <p><strong>Data:</strong> ${new Date().toLocaleString('pt-BR')}</p>
  </div>
</body>
</html>
    `;

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Contrato_ToIndoAli_${bookingData?.packageName?.replace(/\\s+/g, '_') || 'Viagem'}_Reserva_${paymentResult?.reservationId || 'N/A'}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Tela de resultado do pagamento
  if (paymentResult) {
    return (
      <div className="min-h-screen pt-20 bg-gray-50">
        <div className="max-w-2xl mx-auto px-4 py-12">
          <Card>
            <CardContent className="p-8 text-center">
              {paymentResult.type === 'pix' && (
                <>
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Smartphone className="w-10 h-10 text-green-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">PIX Gerado com Sucesso!</h2>
                  <p className="text-gray-600 mb-6">
                    Escaneie o QR Code ou copie o código PIX para realizar o pagamento.
                  </p>
                  
                  {paymentResult.pix_qr_code && (
                    <div className="bg-white p-4 rounded-lg border mb-6 inline-block">
                      <img 
                        src={`data:image/png;base64,${paymentResult.pix_qr_code}`}
                        alt="QR Code PIX"
                        className="w-64 h-64 mx-auto"
                      />
                    </div>
                  )}

                  {paymentResult.pix_copy_paste && (
                    <div className="mb-6">
                      <Label className="text-left block mb-2">Código PIX (Copia e Cola):</Label>
                      <div className="flex items-center gap-2">
                        <Input 
                          value={paymentResult.pix_copy_paste}
                          readOnly
                          className="text-xs"
                        />
                        <Button
                          onClick={() => copyToClipboard(paymentResult.pix_copy_paste)}
                          variant="outline"
                          className="flex-shrink-0"
                        >
                          {copied ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                  )}

                  <div className="text-2xl font-bold text-[#003366] mb-6">
                    {formatPrice(paymentResult.value)}
                  </div>

                  <p className="text-sm text-gray-500 mb-4">
                    Reserva: <strong>{paymentResult.reservationId}</strong>
                  </p>

                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button
                      onClick={() => navigate('/')}
                      className="bg-[#003366] hover:bg-[#002244] text-white"
                    >
                      Voltar para Home
                    </Button>
                    {bookingData?.contractContent && (
                      <Button
                        onClick={handleDownloadContract}
                        variant="outline"
                        className="flex items-center gap-2"
                      >
                        <Download className="w-4 h-4" />
                        Baixar Contrato
                      </Button>
                    )}
                  </div>
                </>
              )}

              {paymentResult.type === 'boleto' && (
                <>
                  <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Barcode className="w-10 h-10 text-blue-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    {paymentResult.installments > 1 
                      ? `Carnê com ${paymentResult.installments} Boletos Gerado!`
                      : 'Boleto Gerado com Sucesso!'
                    }
                  </h2>
                  <p className="text-gray-600 mb-6">
                    {paymentResult.installments > 1 
                      ? 'Clique em cada boleto abaixo para visualizar e imprimir.'
                      : 'Clique no botão abaixo para visualizar e imprimir seu boleto.'
                    }
                  </p>

                  <div className="text-xl font-bold text-[#003366] mb-2">
                    Total: {formatPrice(paymentResult.total_value || paymentResult.value)}
                  </div>
                  
                  {paymentResult.installments > 1 && (
                    <p className="text-sm text-gray-500 mb-4">
                      {paymentResult.installments}x de {formatPrice(paymentResult.installment_value)}
                    </p>
                  )}

                  <p className="text-sm text-gray-500 mb-6">
                    Reserva: <strong>{paymentResult.reservationId}</strong>
                  </p>

                  {/* Múltiplos boletos (carnê) */}
                  {paymentResult.boletos && paymentResult.boletos.length > 0 ? (
                    <div className="space-y-3 mb-6">
                      {paymentResult.boletos.map((boleto, index) => (
                        <div key={index} className="border rounded-lg p-4 bg-gray-50">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-semibold text-gray-900">
                                Parcela {boleto.installment}/{paymentResult.installments}
                              </p>
                              <p className="text-sm text-gray-500">
                                Vencimento: {new Date(boleto.due_date).toLocaleDateString('pt-BR')}
                              </p>
                              <p className="text-sm font-medium text-[#003366]">
                                {formatPrice(boleto.value)}
                              </p>
                            </div>
                            <Button
                              onClick={() => window.open(boleto.boleto_url, '_blank')}
                              size="sm"
                              className="bg-blue-600 hover:bg-blue-700 text-white"
                            >
                              <ExternalLink className="w-4 h-4 mr-1" />
                              Ver Boleto
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    /* Boleto único (fallback) */
                    <div className="flex flex-col gap-3 mb-6">
                      {paymentResult.due_date && (
                        <p className="text-sm text-gray-500">
                          Vencimento: <strong>{new Date(paymentResult.due_date).toLocaleDateString('pt-BR')}</strong>
                        </p>
                      )}
                      <Button
                        onClick={() => window.open(paymentResult.boleto_url, '_blank')}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Visualizar Boleto
                      </Button>
                    </div>
                  )}

                  <Button
                    onClick={() => navigate('/')}
                    variant="outline"
                    className="w-full"
                  >
                    Voltar para Home
                  </Button>
                  {bookingData?.contractContent && (
                    <Button
                      onClick={handleDownloadContract}
                      variant="outline"
                      className="w-full mt-3 flex items-center justify-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Baixar Contrato
                    </Button>
                  )}
                </>
              )}

              {paymentResult.type === 'cartao' && (
                <>
                  <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${
                    paymentResult.success ? 'bg-green-100' : 'bg-yellow-100'
                  }`}>
                    {paymentResult.success ? (
                      <CheckCircle className="w-10 h-10 text-green-600" />
                    ) : (
                      <CreditCard className="w-10 h-10 text-yellow-600" />
                    )}
                  </div>
                  
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    {paymentResult.success ? 'Pagamento Aprovado!' : 'Pagamento em Processamento'}
                  </h2>
                  
                  <p className="text-gray-600 mb-6">
                    {paymentResult.success 
                      ? 'Seu pagamento foi aprovado com sucesso! Você receberá um e-mail com os detalhes da reserva.'
                      : 'Seu pagamento está sendo processado. Você receberá uma confirmação por e-mail em breve.'
                    }
                  </p>

                  <div className="text-2xl font-bold text-[#003366] mb-2">
                    {formatPrice(paymentResult.value)}
                  </div>
                  
                  {paymentResult.installments > 1 && (
                    <p className="text-gray-500 mb-4">
                      {paymentResult.installments}x de {formatPrice(paymentResult.installment_value)}
                    </p>
                  )}

                  <p className="text-sm text-gray-500 mb-6">
                    Reserva: <strong>{paymentResult.reservationId}</strong>
                  </p>

                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button
                      onClick={() => navigate('/')}
                      className="bg-[#003366] hover:bg-[#002244] text-white"
                    >
                      Voltar para Home
                    </Button>
                    {bookingData?.contractContent && (
                      <Button
                        onClick={handleDownloadContract}
                        variant="outline"
                        className="flex items-center gap-2"
                      >
                        <Download className="w-4 h-4" />
                        Baixar Contrato
                      </Button>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!canPurchaseOnline) {
    return (
      <div className="min-h-screen pt-20 bg-gray-50">
        <div className="max-w-2xl mx-auto px-4 py-12">
          <Card className="border-red-200">
            <CardContent className="p-8 text-center">
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Prazo Insuficiente</h2>
              <p className="text-gray-600 mb-6">
                Para compras online, é necessário pelo menos <strong>40 dias</strong> de antecedência.
                Faltam apenas <strong>{daysUntilTrip} dias</strong> para sua viagem.
              </p>
              <Button
                onClick={() => window.open('https://wa.me/5521979652791', '_blank')}
                className="bg-green-500 hover:bg-green-600 text-white"
              >
                Falar no WhatsApp
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-6 md:py-12">
        {/* Progress */}
        <div className="mb-8 overflow-x-auto">
          <div className="flex items-center justify-center space-x-2 md:space-x-4 min-w-max px-4">
            <div className="flex items-center">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-green-500 flex items-center justify-center text-white text-xs md:text-sm">✓</div>
              <span className="ml-1 md:ml-2 text-xs md:text-sm text-gray-500">Seleção</span>
            </div>
            <div className="w-8 md:w-16 h-0.5 bg-green-500"></div>
            <div className="flex items-center">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-green-500 flex items-center justify-center text-white text-xs md:text-sm">✓</div>
              <span className="ml-1 md:ml-2 text-xs md:text-sm text-gray-500">Dados</span>
            </div>
            <div className="w-8 md:w-16 h-0.5 bg-green-500"></div>
            <div className="flex items-center">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-green-500 flex items-center justify-center text-white text-xs md:text-sm">✓</div>
              <span className="ml-1 md:ml-2 text-xs md:text-sm text-gray-500">Embarque</span>
            </div>
            <div className="w-8 md:w-16 h-0.5 bg-green-500"></div>
            <div className="flex items-center">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-green-500 flex items-center justify-center text-white text-xs md:text-sm">✓</div>
              <span className="ml-1 md:ml-2 text-xs md:text-sm text-gray-500">Contrato</span>
            </div>
            <div className="w-8 md:w-16 h-0.5 bg-[#FFD700]"></div>
            <div className="flex items-center">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-[#FFD700] flex items-center justify-center text-gray-900 font-bold text-xs md:text-sm">5</div>
              <span className="ml-1 md:ml-2 font-semibold text-gray-900 text-xs md:text-sm">Pagamento</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-8">
          {/* Formulário de Pagamento */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-6">Escolha a Forma de Pagamento</h2>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  {/* PIX */}
                  <button
                    onClick={() => { setPaymentMethod('pix'); setInstallments(1); }}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      paymentMethod === 'pix' 
                        ? 'border-[#FFD700] bg-yellow-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Smartphone className="w-8 h-8 mx-auto mb-2 text-green-600" />
                    <p className="font-semibold">PIX</p>
                    {pixDiscountEligible ? (
                      <p className="text-xs text-green-600">5% de desconto</p>
                    ) : (
                      <p className="text-xs text-gray-500">À vista</p>
                    )}
                  </button>

                  {/* Boleto */}
                  <button
                    onClick={() => { setPaymentMethod('boleto'); setInstallments(1); }}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      paymentMethod === 'boleto' 
                        ? 'border-[#FFD700] bg-yellow-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Barcode className="w-8 h-8 mx-auto mb-2 text-blue-600" />
                    <p className="font-semibold text-sm">Boleto / PIX Parcelado</p>
                    <p className="text-xs text-gray-500">Até {maxBoletoInstallments}x</p>
                  </button>

                  {/* Cartão */}
                  <button
                    onClick={() => setPaymentMethod('cartao')}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      paymentMethod === 'cartao' 
                        ? 'border-[#FFD700] bg-yellow-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <CreditCard className="w-8 h-8 mx-auto mb-2 text-purple-600" />
                    <p className="font-semibold">Cartão</p>
                    <p className="text-xs text-gray-500">Até 12x</p>
                  </button>
                </div>

                {/* Aviso sobre desconto PIX não elegível (oculto durante Modo Campanha) */}
                {paymentMethod === 'pix' && !pixDiscountEligible && !campaignMode && (
                  <div className="mb-6 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-amber-800">
                        <p className="font-semibold">Desconto PIX não disponível</p>
                        <p>
                          {isAereo 
                            ? 'Para viagens aéreas, o desconto de 5% no PIX é válido apenas para reservas com mais de 4 meses de antecedência.'
                            : 'Para viagens rodoviárias e passeios de 1 dia, o desconto de 5% no PIX é válido apenas para reservas com mais de 2 meses de antecedência.'
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Parcelamento Boleto */}
                {paymentMethod === 'boleto' && maxBoletoInstallments > 1 && (
                  <div className="mb-6">
                    <Label>Parcelamento do Boleto</Label>
                    <Select value={installments.toString()} onValueChange={(v) => setInstallments(parseInt(v))}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: maxBoletoInstallments }, (_, i) => i + 1).map(num => {
                          // Calcular data do último vencimento para mostrar ao usuário
                          const today = new Date();
                          const lastDueDate = new Date(today.getFullYear(), today.getMonth() + num - 1, today.getDate());
                          const lastDueDateStr = lastDueDate.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
                          
                          // Em modo carrinho, exibe a parcela 1 (que é a maior quando há pacotes com menos parcelas)
                          let installmentLabel;
                          if (cartCheckout?.items?.length > 0) {
                            const sched = buildCartInstallmentSchedule(num);
                            const firstParcel = sched[0]?.value || calculateFinalPrice(num).installmentValue;
                            installmentLabel = `${num}x — 1ª parcela ${formatPrice(firstParcel)}`;
                          } else {
                            installmentLabel = `${num}x de ${formatPrice(calculateFinalPrice(num).installmentValue)} sem juros`;
                          }

                          return (
                            <SelectItem key={num} value={num.toString()}>
                              {installmentLabel}
                              {num > 1 && <span className="text-gray-500 ml-1">(última: {lastDueDateStr})</span>}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    {cartCheckout?.items?.length > 0 && installments > 1 ? (
                      <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">
                        <p className="font-semibold mb-2">📋 Cronograma de parcelas (carrinho com múltiplos pacotes):</p>
                        <div className="space-y-1">
                          {buildCartInstallmentSchedule(installments).map((s) => (
                            <div key={s.installment} className="flex justify-between">
                              <span>Parcela {s.installment}/{installments}:</span>
                              <span className="font-semibold">{formatPrice(s.value)}</span>
                            </div>
                          ))}
                        </div>
                        <p className="mt-2 text-amber-800 italic">
                          Pacotes com prazo menor são quitados nas primeiras parcelas (cada parcela é a soma das parcelas individuais de cada pacote ainda ativas).
                        </p>
                      </div>
                    ) : isSpecialInstallmentRule ? (
                      <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
                        <p className="font-semibold">📋 Regra especial para viagens próximas:</p>
                        <p>• 50% no ato da compra</p>
                        <p>• 50% até 7 dias antes da viagem</p>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500 mt-2">
                        Parcelas vencem todo dia {new Date().getDate()} de cada mês
                      </p>
                    )}
                  </div>
                )}

                {/* Formulário Cartão */}
                {paymentMethod === 'cartao' && (
                  <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-4">Dados do Cartão</h3>
                    
                    <div>
                      <Label>Nome no Cartão *</Label>
                      <Input
                        placeholder="NOME COMO ESTÁ NO CARTÃO"
                        value={cardData.holderName}
                        onChange={(e) => setCardData({ ...cardData, holderName: e.target.value.toUpperCase() })}
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label>Número do Cartão *</Label>
                      <Input
                        placeholder="0000 0000 0000 0000"
                        value={cardData.number}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 16);
                          const formatted = value.replace(/(\d{4})/g, '$1 ').trim();
                          setCardData({ ...cardData, number: formatted });
                        }}
                        className="mt-1"
                      />
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Mês *</Label>
                        <Select value={cardData.expiryMonth} onValueChange={(v) => setCardData({ ...cardData, expiryMonth: v })}>
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Mês" />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({ length: 12 }, (_, i) => {
                              const month = (i + 1).toString().padStart(2, '0');
                              return <SelectItem key={month} value={month}>{month}</SelectItem>;
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Ano *</Label>
                        <Select value={cardData.expiryYear} onValueChange={(v) => setCardData({ ...cardData, expiryYear: v })}>
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Ano" />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({ length: 10 }, (_, i) => {
                              const year = (new Date().getFullYear() + i).toString();
                              return <SelectItem key={year} value={year}>{year}</SelectItem>;
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>CVV *</Label>
                        <Input
                          placeholder="123"
                          value={cardData.cvv}
                          onChange={(e) => setCardData({ ...cardData, cvv: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                          className="mt-1"
                          maxLength={4}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>CEP de Cobrança *</Label>
                        <Input
                          placeholder="00000-000"
                          value={cardData.postalCode}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 8);
                            const formatted = value.replace(/(\d{5})(\d)/, '$1-$2');
                            setCardData({ ...cardData, postalCode: formatted });
                          }}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Número do Endereço *</Label>
                        <Input
                          placeholder="123"
                          value={cardData.addressNumber}
                          onChange={(e) => setCardData({ ...cardData, addressNumber: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                    </div>

                    <div>
                      <Label>Parcelas</Label>
                      <Select value={installments.toString()} onValueChange={(v) => setInstallments(parseInt(v))}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: maxCardInstallments }, (_, i) => i + 1).map(num => {
                            const p = calculateFinalPrice(num);
                            return (
                              <SelectItem key={num} value={num.toString()}>
                                {num}x de {formatPrice(p.installmentValue)} {p.interestRate > 0 ? `(${p.interestRate}% juros)` : 'sem juros'}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {/* Cupom de Desconto */}
                <div className="mt-6 pt-6 border-t">
                  <Label className="flex items-center mb-2">
                    <Tag className="w-4 h-4 mr-2" />
                    Cupom de Desconto
                  </Label>
                  {campaignMode ? (
                    <div className="flex items-center p-3 bg-purple-50 border border-purple-200 rounded-lg">
                      <span className="text-purple-700 text-sm">
                        Cupons desabilitados durante a promoção do carrinho.
                      </span>
                    </div>
                  ) : couponBlocked ? (
                    <div className="flex items-center p-3 bg-orange-50 border border-orange-200 rounded-lg">
                      <span className="text-orange-700 text-sm">
                        Cupom não disponível durante promoções ativas.
                      </span>
                    </div>
                  ) : couponApplied ? (
                    <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                      <span className="text-green-700 font-medium">
                        {couponApplied.code} - {couponApplied.value}% OFF
                      </span>
                      <Button variant="ghost" size="sm" onClick={removeCoupon} className="text-red-500">
                        Remover
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        placeholder="Digite seu cupom"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value)}
                      />
                      <Button onClick={applyCoupon} variant="outline">Aplicar</Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Bolsa de Crédito */}
            {(creditBags.normal > 0 || creditBags.limited > 0) && (
              <Card data-testid="credit-bag-card">
                <CardContent className="p-6">
                  <Label className="flex items-center mb-2">
                    <CreditCard className="w-4 h-4 mr-2 text-emerald-600" />
                    Bolsa de Crédito disponível
                  </Label>
                  <p className="text-xs text-gray-500 mb-3">Utilize seu saldo para abater desta compra.</p>
                  <div className="grid grid-cols-1 gap-2">
                    {creditBags.normal > 0 && (
                      <button
                        type="button"
                        data-testid="use-credit-normal-btn"
                        onClick={() => setUseNormalCredit(v => !v)}
                        className={`text-left px-3 py-2 rounded-lg border-2 transition-all ${useNormalCredit ? 'border-emerald-600 bg-emerald-50' : 'border-gray-200 hover:border-emerald-300'}`}
                      >
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-semibold text-emerald-700">Bolsa Normal {useNormalCredit ? '✓' : ''}</span>
                          <span className="text-sm font-bold text-emerald-900">{formatPrice(creditBags.normal)}</span>
                        </div>
                        <p className="text-[10px] text-gray-500">Utilizável integralmente até o valor total.</p>
                      </button>
                    )}
                    {creditBags.limited > 0 && (
                      <button
                        type="button"
                        data-testid="use-credit-limited-btn"
                        onClick={() => setUseLimitedCredit(v => !v)}
                        className={`text-left px-3 py-2 rounded-lg border-2 transition-all ${useLimitedCredit ? 'border-amber-600 bg-amber-50' : 'border-gray-200 hover:border-amber-300'}`}
                      >
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-semibold text-amber-700">Bolsa Limitada {useLimitedCredit ? '✓' : ''}</span>
                          <span className="text-sm font-bold text-amber-900">{formatPrice(creditBags.limited)}</span>
                        </div>
                        <p className="text-[10px] text-gray-500">Utilizável até 30% do valor da nova compra.</p>
                      </button>
                    )}
                  </div>
                  {(useNormalCredit || useLimitedCredit) && (
                    <p className="text-[11px] text-gray-500 mt-2">As duas bolsas podem ser usadas juntas — cada uma respeita a sua regra.</p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Segurança */}
            <div className="flex items-center justify-center text-sm text-gray-500">
              <Shield className="w-4 h-4 mr-2" />
              Pagamento 100% seguro via ASAAS
            </div>
          </div>

          {/* Resumo */}
          <div>
            <Card className="sticky top-24">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Resumo da Reserva</h3>
                
                <div className="space-y-3 text-sm">
                  {cartCheckout?.items?.length > 0 ? (
                    <div className="space-y-2 border-b pb-3 mb-1">
                      {cartCheckout.items.map((it) => (
                        <div key={it.id} className="flex justify-between text-xs">
                          <span className="text-gray-700 truncate mr-2">
                            {it.packageName}
                            <span className="text-gray-400"> ({it.dateLabel})</span>
                          </span>
                          <span className="font-medium text-gray-900 whitespace-nowrap">{formatPrice(it.totalPrice)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Pacote:</span>
                        <span className="font-medium">{bookingData?.packageName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Viajantes:</span>
                        <span className="font-medium">{bookingData?.quantity}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Data:</span>
                        <span className="font-medium">{bookingData?.selectedDate}</span>
                      </div>
                    </>
                  )}
                  
                  <div className="border-t pt-3 mt-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Subtotal:</span>
                      <span>{formatPrice(calculateFinalPrice().baseTotal)}</span>
                    </div>

                    {(() => {
                      const p = calculateFinalPrice();
                      if (p.cartTierDiscount > 0) {
                        return (
                          <div className="flex justify-between text-purple-600">
                            <span>
                              Desconto promoção ({p.cartTierPercent}% ·{' '}
                              {p.cartTierSource === 'daily'
                                ? 'desconto do dia'
                                : p.cartTierPercent === 15 ? '3+ viajantes'
                                : p.cartTierPercent === 10 ? '2 viajantes'
                                : '1 viajante'}):
                            </span>
                            <span>-{formatPrice(p.cartTierDiscount)}</span>
                          </div>
                        );
                      }
                      // Quando o desconto diário já cobriu tudo, mostra aviso explicativo
                      if (campaignMode && p.cartTierSource === 'daily-already-applied' && p.cartTierPercent > 0) {
                        return (
                          <div className="flex justify-between text-purple-500 text-xs italic">
                            <span>Desconto promocional ({p.cartTierPercent}%):</span>
                            <span>já aplicado ao preço</span>
                          </div>
                        );
                      }
                      return null;
                    })()}
                    
                    {couponApplied && calculateFinalPrice().couponDiscount > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>Desconto cupom ({couponApplied.type === 'percentage' ? `${couponApplied.value}%` : 'fixo'}):</span>
                        <span>-{formatPrice(calculateFinalPrice().couponDiscount)}</span>
                      </div>
                    )}

                    {calculateFinalPrice().hasPixDiscount && (
                      <div className="flex justify-between text-green-600">
                        <span>Desconto PIX (5%):</span>
                        <span>-{formatPrice(calculateFinalPrice().pixDiscount)}</span>
                      </div>
                    )}
                    
                    {calculateFinalPrice().interestAmount > 0 && (
                      <div className="flex justify-between text-orange-600">
                        <span>Juros ({calculateFinalPrice().interestRate}%):</span>
                        <span>+{formatPrice(calculateFinalPrice().interestAmount)}</span>
                      </div>
                    )}

                    {calculateFinalPrice().creditUsed > 0 && (
                      <div className="flex justify-between text-emerald-700" data-testid="summary-credit-used">
                        <span>
                          Bolsa de Crédito {calculateFinalPrice().creditKindApplied === 'both' ? '(Normal + Limitada)' : (calculateFinalPrice().creditKindApplied === 'limited' ? '(Limitada)' : '(Normal)')}:
                        </span>
                        <span>-{formatPrice(calculateFinalPrice().creditUsed)}</span>
                      </div>
                    )}
                  </div>

                  <div className="border-t pt-3 mt-3">
                    {(() => {
                      const p = calculateFinalPrice();
                      return (
                        <>
                          <div className="flex justify-between text-lg font-bold">
                            <span>Total:</span>
                            <span className="text-[#003366]">{formatPrice(p.finalPrice)}</span>
                          </div>
                          {installments > 1 && (
                            <p className="text-sm text-gray-500 text-right">
                              {p.installments}x de {formatPrice(p.installmentValue)}
                            </p>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>

                <Button
                  onClick={handlePayment}
                  disabled={!paymentMethod || processing}
                  className="w-full mt-6 bg-[#FFD700] hover:bg-[#FFC700] text-gray-900 font-semibold py-6"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processando...
                    </>
                  ) : (
                    `Pagar ${formatPrice(calculateFinalPrice().finalPrice)}`
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutStep4;
